import {
  RadioGroup,
  RadioTile,
  SelectItem,
  TextInput,
  media,
  useMediaQuery,
} from '@utilitywarehouse/hearth-react';
import { Select } from '@utilitywarehouse/hearth-react';
import { useFormikContext } from 'formik';

import { useFieldProps } from '@hooks/useFieldProps';
import {
  INTRODUCED_BY,
  IntroducedBy,
  ReferralFieldFormValues,
} from '@src/types/attribution';
import { SelectOptions } from '@src/types/index';

import './styles.scss';

const getRadioButtons = ({
  capturePartnerReferral,
  captureConnectorReferral,
}: {
  capturePartnerReferral: boolean;
  captureConnectorReferral: boolean;
}): { label: string; value: IntroducedBy }[] => {
  const options: { label: string; value: IntroducedBy }[] = [];

  const showCustomerExplicitly =
    capturePartnerReferral || captureConnectorReferral;
  const customerLabel = showCustomerExplicitly ? 'Yes, a UW customer' : 'Yes';

  options.push({ label: customerLabel, value: INTRODUCED_BY.CUSTOMER });

  if (capturePartnerReferral) {
    options.push({ label: 'Yes, a UW partner', value: INTRODUCED_BY.PARTNER });
  } else if (captureConnectorReferral) {
    options.push({
      label: 'Yes, a UW connector',
      value: INTRODUCED_BY.CONNECTOR,
    });
  }

  options.push({ label: 'No', value: INTRODUCED_BY.NONE });

  return options;
};

export interface ReferralFieldProps {
  label: string;
  capturePartnerReferral?: boolean;
  captureConnectorReferral: boolean;
  connectorsOptions?: Nullable<SelectOptions>;
}

export const ReferralField = ({
  label,
  capturePartnerReferral = false,
  connectorsOptions,
  captureConnectorReferral = false,
}: ReferralFieldProps) => {
  const {
    values,
    setFieldValue,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitCount,
  } = useFormikContext<ReferralFieldFormValues>();

  const getFieldProps = useFieldProps<ReferralFieldFormValues>({
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
  });

  const radioOptions = getRadioButtons({
    capturePartnerReferral,
    captureConnectorReferral,
  });

  const selectedValue = values.introducedBy;
  const isMobile = useMediaQuery(media.below('tablet'));
  const MIN_OPTIONS_FOR_STACK = 3;
  const shouldStackOnMobile =
    isMobile && radioOptions.length >= MIN_OPTIONS_FOR_STACK;

  return (
    <>
      <RadioGroup
        onValueChange={(val: string) => setFieldValue('introducedBy', val)}
        {...getFieldProps('introducedBy')}
        label={label}
        direction={shouldStackOnMobile ? 'column' : 'row'}
        className="partnerAppointment__radioField"
        onChange={() => {
          setFieldValue('customerReferral', '');
          setFieldValue('connectorReferral', '');
          setFieldValue('partnerReferral', '');
        }}
        contentWidth="100%"
      >
        {radioOptions.map((option) => (
          <RadioTile
            id={option.value}
            data-testid={option.value}
            label={option.label}
            value={option.value}
            key={option.value}
          />
        ))}
      </RadioGroup>

      {selectedValue === INTRODUCED_BY.CUSTOMER && (
        <TextInput
          required
          data-testid="customerReferral"
          className="referral__field"
          {...getFieldProps('customerReferral')}
          label="Enter UW account number of the referring customer"
        />
      )}
      {captureConnectorReferral &&
        selectedValue === INTRODUCED_BY.CONNECTOR &&
        connectorsOptions && (
          <Select
            className="referral__select"
            label="Select connector"
            placeholder="Select connector"
            required
            value={values.connectorReferral}
            onValueChange={(val: string) =>
              setFieldValue('connectorReferral', val)
            }
            validationStatus={
              errors.connectorReferral && submitCount > 0
                ? 'invalid'
                : undefined
            }
            validationText={
              submitCount > 0 ? errors.connectorReferral : undefined
            }
          >
            {connectorsOptions?.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </Select>
        )}
      {capturePartnerReferral && selectedValue === INTRODUCED_BY.PARTNER && (
        <TextInput
          required
          data-testid="partnerReferral"
          className="referral__field"
          {...getFieldProps('partnerReferral')}
          label="Enter UW partner number"
        />
      )}
    </>
  );
};
